import React from "react";
import { Helmet } from "react-helmet-async";
import { Box, Typography, Link as MuiLink } from "@mui/material";
import { Link } from "react-router-dom";
import Footer from "../Components/Footer/Footer";

const sectionSx = { mt: 4 };
const bodySx = { lineHeight: 1.8, mb: 1.5, fontFamily: "Roboto, Helvetica, Arial, sans-serif" };

const PrivacyPolicy = () => {
  return (
    <>
      <Helmet>
        <title>Privacy Policy | Đông Hưng Viên Bakery</title>
        <meta
          name="description"
          content="How Đông Hưng Viên Bakery collects, uses, and protects information gathered through this website."
        />
      </Helmet>
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          background: "#fff",
          pt: { xs: 14, md: 18 },
          pb: 8,
        }}
      >
        <Box sx={{ maxWidth: "860px", width: "90%", color: "#222" }}>
          <Typography variant="h3" sx={{ fontSize: { xs: "28px", md: "36px" }, fontWeight: 700, mb: 1 }}>
            Privacy Policy
          </Typography>
          <Typography sx={{ color: "#666", mb: 3 }}>Effective date: August 4, 2026</Typography>

          <Typography sx={bodySx}>
            Đông Hưng Viên Bakery, operated by Dong Hung Vien, Inc. (“DHV Bakery,” “we,” “us,” or
            “our”), operates a single bakery location at 8536 Westminster Blvd, Westminster, CA
            92683, and the website located at donghungvienbakery.com (the “Site”). This Privacy
            Policy explains what information we collect through the Site, how we use it, and the
            choices available to you.
          </Typography>
          <Typography sx={bodySx}>
            The Site and our advertising are directed to customers located in our local Southern
            California service area. We do not knowingly direct the Site or our advertising to, or
            collect information from, residents of the European Union, United Kingdom, or other
            jurisdictions outside the United States. If you access the Site from outside the United
            States, you do so on your own initiative and are responsible for compliance with local law.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>Information We Collect</Typography>
          <Typography sx={bodySx}>
            <strong>Information you give us directly.</strong> When you use our Contact, Vietnamese
            Tea Ceremony booking, or Wholesale inquiry forms, we (through our form processor,
            Formspree) collect the information you enter, such as your name, email address, phone
            number, and the details of your message, event, or business inquiry.
          </Typography>
          <Typography sx={bodySx}>
            <strong>Information collected automatically.</strong> Like most websites, the Site
            automatically logs standard technical data (such as your browser type, device
            information, IP address, pages viewed, and referring URL).
          </Typography>
          <Typography sx={bodySx}>
            <strong>Advertising cookies and pixels.</strong> We use the Meta (Facebook/Instagram)
            Pixel and Meta Conversions API to measure the effectiveness of our local advertising and
            to understand how visitors interact with the Site. These tools set first-party cookies
            (named <code>_fbp</code> and <code>_fbc</code>) and may share technical identifiers (such
            as a hashed or pseudonymous browser ID, the page you visited, and, if you arrived from a
            Facebook/Instagram ad, a click identifier) with Meta. Our advertising campaigns are
            geographically limited to roughly a 20-mile radius of our store, but the underlying
            advertising technology is provided by Meta and is subject to Meta's own privacy practices.
          </Typography>
          <Typography sx={bodySx}>
            <strong>Google Reviews.</strong> The Site displays publicly available Google review
            content for our business. We do not collect personal information from you through this
            feature.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>How We Use Information</Typography>
          <Typography sx={bodySx}>We use the information described above to:</Typography>
          <Box component="ul" sx={{ pl: 3, ...bodySx }}>
            <li>Respond to inquiries and fulfill booking and wholesale requests;</li>
            <li>Operate, maintain, and improve the Site;</li>
            <li>Measure and improve the performance of our local paid advertising; and</li>
            <li>Comply with legal obligations and protect our rights.</li>
          </Box>

          <Typography variant="h5" sx={sectionSx}>Who We Share Information With</Typography>
          <Typography sx={bodySx}>
            We do not sell your personal information. We share information only with the service
            providers who help us run the Site and our business, including: Formspree (form
            submission processing), Meta Platforms, Inc. (advertising measurement, described above),
            and Google (review display, maps, and fonts). Online orders placed through our “Order
            Now” button are processed entirely by Toast, Inc. on its own platform and are governed by
            Toast's privacy policy, not this one.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>Cookies &amp; Do Not Track</Typography>
          <Typography sx={bodySx}>
            The Site uses cookies as described above to support advertising measurement. You can
            control or delete cookies through your browser settings, and you can review or limit
            Meta's use of your information for ads through your{" "}
            <MuiLink href="https://www.facebook.com/ads/preferences" target="_blank" rel="noopener noreferrer">
              Meta ad preferences
            </MuiLink>{" "}
            or the{" "}
            <MuiLink href="https://optout.aboutads.info/" target="_blank" rel="noopener noreferrer">
              Digital Advertising Alliance opt-out page
            </MuiLink>
            . The Site does not currently respond to browser “Do Not Track” signals.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>Your Choices &amp; Rights</Typography>
          <Typography sx={bodySx}>
            You may ask us what personal information we have collected about you, request that we
            correct or delete it, or ask us to stop using it for advertising measurement, by
            contacting us using the information below or through our{" "}
            <MuiLink component={Link} to="/contact">Contact page</MuiLink>. We will verify your
            request and respond within a reasonable time.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>Changes to This Policy</Typography>
          <Typography sx={bodySx}>
            We may update this Privacy Policy from time to time. The “Effective date” above reflects
            the most recent revision.
          </Typography>

          <Typography variant="h5" sx={sectionSx}>Contact Us</Typography>
          <Typography sx={bodySx}>
            Đông Hưng Viên Bakery (Dong Hung Vien, Inc.)
            <br />
            8536 Westminster Blvd, Westminster, CA 92683
            <br />
            Phone:{" "}
            <MuiLink href="tel:7148914404">(714) 891-4404</MuiLink>
            <br />
            Email:{" "}
            <MuiLink href="mailto:info@donghungvienbakery.com">info@donghungvienbakery.com</MuiLink>
          </Typography>
        </Box>
      </Box>
      <Footer />
    </>
  );
};

export default PrivacyPolicy;
